/**
 * bulk-element-ops.js — pure HTML transforms for repeat-root bulk operations
 * (Polish 16C1). String in, string out: no store, no DOM globals beyond the
 * DOMParser the primitives already use, no eventBus. element-actions.js owns
 * the commit; this module owns only the chaining rules.
 *
 * The shape the plan mandates: `removeNearEid` / `duplicateNearEid` are pure
 * string→string, so N units become N transforms over ONE accumulating HTML
 * string and a single `store.batch([html, overrides], 'Delete N elements')`.
 * `pruneOverrides` / `cloneOverridesViaEidMap` therefore run once, on the
 * final result, not once per unit.
 *
 * Two properties make the chain safe where the section-level equivalent needed
 * an index calculus (see sections/bulk-operations.js):
 *
 *  - Units are addressed by EID, not by index, so removing unit 1 cannot shift
 *    the address of unit 3. No descending/ascending ordering rule is needed.
 *  - `duplicateNearEid` re-derives its "used eids" set from the HTML it is
 *    handed, so chained duplicates never collide — clone k lands immediately
 *    after original k, which is what makes N=1 byte-identical to the single
 *    duplicate path.
 *
 * All-or-nothing: a unit that fails to resolve aborts the whole transform and
 * returns null. A bulk op must never half-apply.
 */

import { duplicateNearEid, removeNearEid } from './element-insert.js';

/**
 * Remove every unit in `unitEids` from `sectionHtml`.
 * @param {string} sectionHtml
 * @param {string[]} unitEids — any eid inside each unit
 * @param {string} itemSelector
 * @returns {{updatedHtml: string, removedEids: string[]}|null}
 */
export function buildBulkElementDelete(sectionHtml, unitEids, itemSelector) {
  const eids = _dedupe(unitEids);
  if (!sectionHtml || !itemSelector || eids.length === 0) return null;

  let html = sectionHtml;
  const removedEids = [];
  for (const eid of eids) {
    const result = removeNearEid(html, eid, { itemSelector });
    if (!result.updatedHtml) return null;
    html = result.updatedHtml;
    removedEids.push(...result.removedEids);
  }
  return { updatedHtml: html, removedEids };
}

/**
 * Duplicate every unit in `unitEids`, each clone landing immediately after its
 * own original. The merged eidMap covers every source→clone pair across all
 * units, so ONE `cloneOverridesViaEidMap` call carries the styling over.
 * @returns {{updatedHtml: string, eidMap: Record<string,string>,
 *   newUnitEids: string[], newPrimaryEids: string[]}|null}
 */
export function buildBulkElementDuplicate(sectionHtml, unitEids, itemSelector, options = {}) {
  const eids = _dedupe(unitEids);
  if (!sectionHtml || !itemSelector || eids.length === 0) return null;

  let html = sectionHtml;
  const eidMap = {};
  const newUnitEids = [];
  const newPrimaryEids = [];
  for (const eid of eids) {
    const result = duplicateNearEid(html, eid, {
      itemSelector,
      position: 'after',
      editableIdRepairs: options.editableIdRepairs
    });
    if (!result.updatedHtml) return null;
    html = result.updatedHtml;
    Object.assign(eidMap, result.eidMap || {});
    // An eid INSIDE the clone. `eidMap[eid]` is preferred because it is the
    // exact counterpart of the eid the caller addressed the source by; callers
    // re-resolve it to a unit identity via siblingUnitsNearEid, so any eid in
    // the clone is a correct answer and the fallbacks cannot mis-point.
    const cloneUnitEid = result.eidMap?.[eid] || result.newRootEid || result.newPrimaryEid || null;
    if (cloneUnitEid) newUnitEids.push(cloneUnitEid);
    if (result.newPrimaryEid) newPrimaryEids.push(result.newPrimaryEid);
  }
  return { updatedHtml: html, eidMap, newUnitEids, newPrimaryEids };
}

/** Preserve caller order, drop falsy and repeats. */
function _dedupe(ids) {
  const seen = new Set();
  const out = [];
  for (const id of Array.isArray(ids) ? ids : []) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}
